import React, { useState, useCallback } from "react";
import { Button } from "../ui/button";
import { useSocial } from "../../contexts/SocialContext";
import { Users, X, Check, Loader2, Clock3 } from "lucide-react";

const modeLabel = (mode) => (mode === "virtual" ? "online" : "in person");

/** A face and a name, at the size the feed card's own avatars use. */
const PartnerFace = ({ partner, className = "" }) =>
  partner.photo_url ? (
    <img src={partner.photo_url} alt="" className={`h-5 w-5 rounded-full ${className}`} />
  ) : (
    <div
      className={`h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-medium ${className}`}
    >
      {partner.name?.[0] || "?"}
    </div>
  );

/**
 * Who else was at this workout.
 *
 * Three states share the strip, because they are the same fact at different
 * stages: confirmed partners read as plain names, a claim you sent reads as
 * waiting, and a claim aimed at you asks a question. Anyone else scrolling the
 * feed sees only the confirmed ones — the server never sends them the rest.
 */
export default function SessionPartners({ post, onUpdate }) {
  const { respondToSessionLink, unlinkSessions } = useSocial();
  const [busyId, setBusyId] = useState(null);

  const partners = post?.partners || [];
  const confirmed = partners.filter((p) => p.status === "accepted");
  const awaitingMe = partners.filter((p) => p.awaiting_me);
  const awaitingThem = partners.filter((p) => p.requested_by_me);

  const respond = useCallback(
    async (linkId, accept) => {
      setBusyId(linkId);
      try {
        await respondToSessionLink(linkId, accept);
        onUpdate?.();
      } catch (err) {
        console.error("Respond to session link failed:", err);
      } finally {
        setBusyId(null);
      }
    },
    [respondToSessionLink, onUpdate],
  );

  const remove = useCallback(
    async (linkId) => {
      setBusyId(linkId);
      try {
        await unlinkSessions(linkId);
        onUpdate?.();
      } catch (err) {
        console.error("Unlink sessions failed:", err);
      } finally {
        setBusyId(null);
      }
    },
    [unlinkSessions, onUpdate],
  );

  if (partners.length === 0) return null;

  return (
    <div className="space-y-2 mb-3">
      {confirmed.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <Users className="h-3.5 w-3.5 flex-shrink-0" />
            Trained with
          </span>
          {confirmed.map((partner) => (
            <span
              key={`${partner.user_id}-${partner.link_id || "live"}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-muted/50 pl-0.5 pr-2 py-0.5"
            >
              <PartnerFace partner={partner} />
              <span className="font-medium">{partner.name}</span>
              <span className="text-muted-foreground">{modeLabel(partner.mode)}</span>
              {partner.can_remove && (
                <button
                  type="button"
                  onClick={() => remove(partner.link_id)}
                  disabled={busyId === partner.link_id}
                  aria-label={`Remove ${partner.name} from this workout`}
                  className="text-muted-foreground hover:text-destructive transition-colors"
                >
                  {busyId === partner.link_id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <X className="h-3 w-3" />
                  )}
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {awaitingThem.map((partner) => (
        <div
          key={`sent-${partner.link_id}`}
          className="flex items-center gap-2 text-xs text-muted-foreground"
        >
          <Clock3 className="h-3.5 w-3.5 flex-shrink-0" />
          <PartnerFace partner={partner} className="opacity-60" />
          <span className="flex-1 min-w-0 truncate">
            Waiting on {partner.name} to confirm
          </span>
          <button
            type="button"
            onClick={() => remove(partner.link_id)}
            disabled={busyId === partner.link_id}
            className="hover:text-destructive transition-colors flex-shrink-0"
          >
            {busyId === partner.link_id ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              "Cancel"
            )}
          </button>
        </div>
      ))}

      {awaitingMe.map((partner) => (
        <div
          key={`ask-${partner.link_id}`}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-2"
        >
          <PartnerFace partner={partner} />
          <span className="flex-1 min-w-0 text-xs">
            <span className="font-medium">{partner.name}</span> says you trained
            together {modeLabel(partner.mode)}.
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              size="sm"
              className="h-7 text-xs px-2"
              onClick={() => respond(partner.link_id, true)}
              disabled={busyId === partner.link_id}
            >
              {busyId === partner.link_id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Confirm
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs px-2 text-muted-foreground"
              onClick={() => respond(partner.link_id, false)}
              disabled={busyId === partner.link_id}
            >
              Dismiss
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
